/**
 * 表示一个3x3的变换矩阵，用于处理2D空间中
 * 的平移、旋转、缩放等仿射变换。
 */
export class Matrix {

    /** 
     * 存储3x3矩阵数据的二维数组 
     * 索引结构: [row][column]
     */
    public data: number[][];

    /**
     * 创建一个新的矩阵对象
     * 默认初始化为单位矩阵
     */
    constructor() {
        this.data = [
            [1, 0, 0],
            [0, 1, 0],
            [0, 0, 1]
        ];
    }

    /**
     * 创建一个新的单位矩阵
     * @returns 新的单位矩阵实例
     */
    static identity(): Matrix {
        return new Matrix();
    }

    /**
     * 创建一个平移变换矩阵
     * @param x - X轴平移距离
     * @param y - Y轴平移距离
     * @returns 新的平移变换矩阵
     */
    static translation(x: number, y: number): Matrix {
        const m = new Matrix();
        m.data[0][2] = x;
        m.data[1][2] = y;
        return m;
    }

    /**
     * 创建一个旋转变换矩阵
     * @param angle - 旋转角度（弧度制）
     * @returns 新的旋转变换矩阵
     */
    static rotation(angle: number): Matrix {
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const m = new Matrix();
        m.data[0][0] = cos;
        m.data[0][1] = -sin;
        m.data[1][0] = sin;
        m.data[1][1] = cos;
        return m;
    }

    /**
     * 创建一个缩放变换矩阵
     * @param sx - X轴方向的缩放因子
     * @param sy - Y轴方向的缩放因子
     * @returns 新的缩放变换矩阵
     */
    static scaling(sx: number, sy: number): Matrix {
        const m = new Matrix();
        m.data[0][0] = sx;
        m.data[1][1] = sy;
        return m;
    }

    /**
     * 将当前矩阵与另一个矩阵相乘
     * 计算 this * other 的结果
     * @param other - 要相乘的另一个矩阵
     * @returns 矩阵乘法的结果矩阵
     */
    multiply(other: Matrix): Matrix {
        const result = new Matrix();
        for (let i = 0; i < 3; i++) {
            for (let j = 0; j < 3; j++) {
                result.data[i][j] = 0;
                for (let k = 0; k < 3; k++) {
                    result.data[i][j] += this.data[i][k] * other.data[k][j];
                }
            }
        }
        return result;
    }

    /**
     * 创建当前矩阵的深拷贝
     * @returns 当前矩阵的副本
     */
    clone(): Matrix {
        const result = new Matrix();
        for (let i = 0; i < 3; i++) {
            for (let j = 0; j < 3; j++) {
                result.data[i][j] = this.data[i][j];
            }
        }
        return result;
    }

    /**
     * 使用当前矩阵变换一个点
     * @param x - 点的 x 坐标
     * @param y - 点的 y 坐标
     * @returns 变换后的点坐标 
     */
    public transformPoint(x: number, y: number): { x: number; y: number } {
        const newX = this.data[0][0] * x + this.data[0][1] * y + this.data[0][2];
        const newY = this.data[1][0] * x + this.data[1][1] * y + this.data[1][2];
        return { x: newX, y: newY };
    }
}